/**
 * omnyqra: translatable default texts of the emcn components
 * (Omnyqra/sim#67), see FORK.md.
 *
 * emcn stays free of the app and of any i18n library. The app wraps its tree
 * once in {@link EmcnLabelsProvider} with translated texts; without a provider
 * (tests, emcn on its own) the English defaults apply as before. An explicit
 * prop on a component still wins over the provider.
 *
 * @example
 * ```tsx
 * <EmcnLabelsProvider labels={{ cancel: 'Abbrechen', close: 'Schließen' }}>
 *   {children}
 * </EmcnLabelsProvider>
 * ```
 */

'use client'

import * as React from 'react'
import { filterUndefined } from '@sim/utils/object'

export interface EmcnLabels {
  /** BCP 47 locale for dates the components format themselves. */
  locale: string
  /** First column of the calendar grid: 0 = Sunday, 1 = Monday. */
  weekStartsOn: 0 | 1
  /** Clock of the time fields and chart timestamps: 12 = `2:30 PM`, 24 = `14:30` (Omnyqra/sim#78). */
  hourCycle: 12 | 24
  /** Dismiss button of `ChipModalFooter`, `ChipConfirmModal` and `ProgressItem`. */
  cancel: string
  /** Accessible label of the close button in modal headers. */
  close: string
  /** Screen-reader title of a `ChipModal` without its own `srTitle`. */
  dialog: string
  /** Screen-reader title of a `ChipConfirmModal` whose title is not a string. */
  confirm: string
  /** "All" option of `ChipSelect`, `Combobox` and `ChipDropdown`. */
  all: string
  /** Wizard footer buttons; `back` also labels the popover back row. */
  back: string
  next: string
  done: string
  /** Screen-reader title and description of a `Wizard` without its own. */
  wizard: string
  dismiss: string
  retry: string
  tryAgain: string
  /** Trigger placeholder of `ChipSelect`, `Combobox` and `ChipDropdown`. */
  select: string
  /** Search field placeholder of the pickers and `PopoverSearch`. */
  search: string
  copy: string
  copied: string
  copyToClipboard: string
  showPassword: string
  hidePassword: string
  dropFilesOrBrowse: string
  dropFiles: string
  invalidSelection: string
  enterEmails: string
  /** Placeholder of email and tag inputs once they hold an entry. */
  addAnother: string
  invalidEmail: string
  invalidEmailOrDomain: string
  searchAllOptions: string
  loadMore: string
  searchingOptions: string
  loadingOptions: string
  noMatchesInPartialResults: string
  noMatchesInLoadedOptions: string
  noOptionsLoaded: string
  noMatchingOptions: string
  noOptionsAvailable: string
  noResults: string
  showingPartialResults: string
  openOptions: string
  closeOptions: string
  time: string
  selectTime: string
  selectDate: string
  selectDateRange: string
  today: string
  clear: string
  apply: string
  /** Between the two times of a range. */
  to: string
  previousMonth: string
  nextMonth: string
  /** Twelve month names, January first. */
  months: readonly string[]
  /** Seven short weekday names, Sunday first; the calendar rotates them by `weekStartsOn`. */
  weekdays: readonly string[]
  chartValue: string
  chartValuesByDate: string
  chartTotal: string
  chartDate: string
  noData: string
  loadingValue: string
  /** Screen-reader text while a chart loads; `title` is the chart's title. */
  loadingChart: (title?: string) => string
  expand: string
  collapse: string
  moreInformation: string
  mediaViewer: string
  closeMediaViewer: string
  mediaZoom: string
  zoomOut: string
  zoomIn: string
  mediaControls: string
  seekVideo: string
  play: string
  pause: string
  mute: string
  unmute: string
  newTab: string
  tabs: string
  enterValues: string
  tagInput: string
  addTag: string
  uploadFile: string
  dropFile: string
  dismissNotification: string
  notifications: string
  /** Screen-reader note on a required picker. */
  required: string
  /** Trigger label of a multi-select with several values. */
  selectedCount: (count: number) => string
  /** Accessible label of a chart legend entry. */
  highlightSeries: (label: string, value?: string) => string
  /** Accessible label of a tab with unseen activity. */
  backgroundActivity: (title: string) => string
  /** New-tab tooltip once the strip is full. */
  maxTabs: (max: number) => string
  /** Close button of a tab. */
  closeTab: (title: string) => string
  /** Remove icon of a tag. */
  removeTag: (value: string) => string
  /** Zoom reset button of the lightbox. */
  resetZoom: (percent: number) => string
  /** Accessible value of the lightbox video seek slider, e.g. `0:12 of 1:30`. */
  playbackPosition: (current: string, total: string) => string
}

const DEFAULT_EMCN_LABELS: EmcnLabels = {
  locale: 'en-US',
  weekStartsOn: 0,
  hourCycle: 12,
  cancel: 'Cancel',
  close: 'Close',
  dialog: 'Dialog',
  confirm: 'Confirm',
  all: 'All',
  back: 'Back',
  next: 'Next',
  done: 'Done',
  wizard: 'Multi-step wizard',
  dismiss: 'Dismiss',
  retry: 'Retry',
  tryAgain: 'Try again',
  select: 'Select...',
  search: 'Search...',
  copy: 'Copy',
  copied: 'Copied!',
  copyToClipboard: 'Copy to clipboard',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  dropFilesOrBrowse: 'Drop files here or click to browse',
  dropFiles: 'Drop files here',
  invalidSelection: 'Invalid selection.',
  enterEmails: 'Enter emails',
  addAnother: 'Add another',
  invalidEmail: 'Invalid email format',
  invalidEmailOrDomain: 'Invalid email or domain',
  searchAllOptions: 'Search all options',
  loadMore: 'Load more',
  searchingOptions: 'Searching options...',
  loadingOptions: 'Loading options...',
  noMatchesInPartialResults: 'No matches in partial results',
  noMatchesInLoadedOptions: 'No matches in loaded options',
  noOptionsLoaded: 'No options loaded',
  noMatchingOptions: 'No matching options found',
  noOptionsAvailable: 'No options available',
  noResults: 'No results',
  showingPartialResults: 'Showing partial results',
  openOptions: 'Open options',
  closeOptions: 'Close options',
  time: 'Time',
  selectTime: 'Select time',
  selectDate: 'Select date',
  selectDateRange: 'Select date range',
  today: 'Today',
  clear: 'Clear',
  apply: 'Apply',
  to: 'to',
  previousMonth: 'Previous month',
  nextMonth: 'Next month',
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  weekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  chartValue: 'Value',
  chartValuesByDate: 'Values by date',
  chartTotal: 'Total',
  chartDate: 'Date',
  noData: 'No data',
  loadingValue: 'Loading value',
  loadingChart: (title) => `Loading ${title ?? 'chart'}`,
  expand: 'Expand',
  collapse: 'Collapse',
  moreInformation: 'More information',
  mediaViewer: 'Media viewer',
  closeMediaViewer: 'Close media viewer',
  mediaZoom: 'Media zoom',
  zoomOut: 'Zoom out',
  zoomIn: 'Zoom in',
  mediaControls: 'Media controls',
  seekVideo: 'Seek video',
  play: 'Play',
  pause: 'Pause',
  mute: 'Mute',
  unmute: 'Unmute',
  newTab: 'New tab',
  tabs: 'Tabs',
  enterValues: 'Enter values',
  tagInput: 'Tag input',
  addTag: 'Add tag',
  uploadFile: 'Upload file',
  dropFile: 'Drop file here',
  dismissNotification: 'Dismiss notification',
  notifications: 'Notifications',
  required: 'Required.',
  selectedCount: (count) => `${count} selected`,
  highlightSeries: (label, value) => `Highlight ${label}${value ? `: ${value}` : ''}`,
  backgroundActivity: (title) => `${title}, Background activity`,
  maxTabs: (max) => `Maximum of ${max} tabs`,
  closeTab: (title) => `Close ${title}`,
  removeTag: (value) => `Remove ${value}`,
  resetZoom: (percent) => `Reset zoom (${percent}%)`,
  playbackPosition: (current, total) => `${current} of ${total}`,
}

const EmcnLabelsContext = React.createContext<EmcnLabels>(DEFAULT_EMCN_LABELS)

interface EmcnLabelsProviderProps {
  /** Texts to replace; missing keys keep the English default. Pass a stable (memoized) object. */
  labels: Partial<EmcnLabels>
  children: React.ReactNode
}

export function EmcnLabelsProvider({ labels, children }: EmcnLabelsProviderProps) {
  const value = React.useMemo(
    () => ({ ...DEFAULT_EMCN_LABELS, ...filterUndefined(labels) }),
    [labels]
  )
  return <EmcnLabelsContext.Provider value={value}>{children}</EmcnLabelsContext.Provider>
}

/** Default texts for emcn components; the English ones without a provider. */
export function useEmcnLabels(): EmcnLabels {
  return React.useContext(EmcnLabelsContext)
}
